import { Button, Loader, MatchUp, Paper, Typography, createCssVariableString } from "best-common-react";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { getEvent } from "../../../api/RequesTixApi";
import { useTicketRequest } from "../../../contexts/TicketRequestContext";
import { UserAllotmentCountDTO } from "../../../types/Allotment";
import { EventTimingTypeDTO } from "../../../types/EventTiming";
import { GameDTO, GameTime } from "../../../types/Game";
import { RequestTypeDesc } from "../../../types/RequestType";
import { SeasonTypeCode } from "../../../types/Season";
import { getSeasonTypeForGame } from "../../../util/SeasonUtil";
import { isBusiness } from "../../../util/TicketRequestUtil";
import { Offset, useTimeUntil, useTimezone } from "../../../util/TimeUtil";
import TimeRemaining from "../card/TimeRemaining";
import TicketRequestSeasonSelect from "./TicketRequestSeasonSelect";
import EventCanceled from "../misc/EventCanceled";

const Container = styled.div`
  min-width: 22rem;
  color: ${createCssVariableString("onColorsOnSurfaceLow")};
  @media (max-width: 767px) {
    width: 100%;
  }
  @media (min-width: 768px) {
    margin-right: 1rem;
  }
`;

type RequestInformationProps = {
  originalGamePk?: number | string;
  setAccessible?: (value: boolean) => void;
  userId: number;
  allotments: UserAllotmentCountDTO[];
  setAllotments: (value: UserAllotmentCountDTO[]) => void;
  type: RequestTypeDesc;
  changeGame?: any;
};

const RequestInformation: React.FC<RequestInformationProps> = ({
  originalGamePk,
  setAccessible,
  userId,
  allotments,
  setAllotments,
  type,
  changeGame = null,
}) => {
  const { game, fetchingGame } = useTicketRequest();
  const [originalGameLoading, setOriginalGameLoading] = useState<boolean>(true);
  const [originalGame, setOriginalGame] = useState<GameDTO>();
  const [gameTime, setGameTime] = useState<GameTime>({});
  const [originalGameTime, setOriginalGameTime] = useState<GameTime>({});
  const [offset, setOffset] = useState<Offset>({});
  const [_allotment, setAllotment] = useState({});

  useEffect(() => {
    if (!!game?.gamePk) {
      const gameDate = !!game.rescheduleDate ? game.rescheduleDate : game.gameDate;
      setGameTime(useTimezone(gameDate, game.venue.timeZone, game.status));
      const cutoff: EventTimingTypeDTO = isBusiness(type) ? game.cutoff.business : game.cutoff.personal;
      const { offset, accessible } = useTimeUntil(gameDate, game.access, cutoff);
      setOffset(offset);
      setAccessible?.(accessible);
    }
  }, [game]);

  useEffect(() => {
    setOriginalGameLoading(true);
    if (originalGamePk) {
      getEvent(originalGamePk).then((res) => {
        setOriginalGame(res);
        setOriginalGameTime(useTimezone(res.gameDate, res.venue.timeZone));
        setOriginalGameLoading(false);
      });
    }
  }, [originalGamePk]);

  useEffect(() => {
    if (game?.gameType && allotments) {
      const seasonType: SeasonTypeCode = getSeasonTypeForGame(game);
      const allot = allotments.find((allotment) => {
        const correctSeason: boolean = seasonType.id === allotment.season.seasonType.seasonTypeId;
        let correctType = true;
        if (type) {
          if (isBusiness(type)) {
            correctType = allotment.allotmentType.allotmentTypeId === 2;
          } else {
            correctType = allotment.allotmentType.allotmentTypeId === 1;
          }
        }
        return correctSeason && correctType;
      });
      if (allot) {
        setAllotment(allot);
      }
    }
  }, [allotments, game]);

  return (
    <Container>
      <Paper className="bcr-mb-2" styles={{ background: "var(--color-surface-lowest)" }}>
        {!fetchingGame && !!gameTime && !!game ? (
          <div className="d-flex flex-column bcr-gap-1">
            <div className="d-flex justify-content-between align-items-start">
              <div className="d-flex flex-column align-items-start bcr-gap-1">
                <div className="d-flex align-items-center bcr-gap-2">
                  <Typography variant="bodyLarge" elementStyles={{ weight: "bold" }}>
                    {gameTime.date}
                  </Typography>
                  <Typography variant="bodyLarge">
                    {gameTime.day}
                    <span className="bcr-ms-2">{gameTime.time}</span>
                    <EventCanceled game={game} />
                  </Typography>
                </div>
              </div>
              <MatchUp
                home={game.teams.home.team}
                away={game.teams.away.team}
                description={game.seriesDescription}
                useSpots={true}
              />
            </div>

            <Typography>
              {game.venue?.name} &nbsp;
              {game.venue?.location?.city}
              {game.venue?.location?.city && game.venue?.location?.stateAbbrev ? ", " : ""}
              {game.venue?.location?.stateAbbrev}
            </Typography>
            {changeGame ? (
              <div className="d-flex justify-content-md-center justify-content-start">
                <Button variant="link" onClick={changeGame}>
                  change game
                </Button>
              </div>
            ) : (
              <TimeRemaining offset={offset} isPersonal={!isBusiness(type)} inline />
            )}
          </div>
        ) : (
          <Loader type="pulse" size="16px" />
        )}
      </Paper>
      {!originalGameLoading && originalGame && originalGameTime && (
        <Paper styles={{ background: "var(--color-surface-low)" }}>
          <Typography variant="bodySmall">Original selection</Typography>
          <div>
            <div className="d-flex justify-content-between">
              <div className="d-flex align-items-start bcr-gap-2">
                <Typography variant="bodyLarge" elementStyles={{ weight: "bold" }}>
                  {originalGameTime.date}
                </Typography>
                <Typography variant="bodyLarge">
                  {originalGameTime.day}
                  <span className="bcr-ms-2">{originalGameTime.time}</span>
                </Typography>
              </div>
              <MatchUp
                home={originalGame.teams.home.team}
                away={originalGame.teams.away.team}
                description={originalGame.seriesDescription}
                useSpots={true}
              />
            </div>
          </div>
        </Paper>
      )}
      <div style={{ height: "5.625rem" }} className="bcr-mt-3">
        {!fetchingGame && !!game ? (
          <TicketRequestSeasonSelect game={game} userId={userId} setAllotments={setAllotments} type={type} />
        ) : (
          <Loader type="pulse" size="16px" />
        )}
      </div>
    </Container>
  );
};

export default RequestInformation;
